/**
 * How loud the reader is, measured in the browser on the microphone stream.
 *
 * Pure functions only: no Web Audio, no timers. The analyser that feeds them
 * lives in services/core/voice-level-monitor.ts, and the controller that ties
 * them to a reading in stores/controllers/voice-level.controller.ts.
 *
 * Every number that decides "too quiet" or "clipping" is here, in one place,
 * so they can be tuned from the READING_VOICE_LEVEL rows later.
 */
export const VOICE_LEVEL = {
  /** How often the level is read, in ms. */
  SAMPLE_INTERVAL_MS: 100,
  /** RMS below this is "quiet" (amber on the meter, counts toward speak up). */
  QUIET_DBFS: -45,
  /** A peak above this is "clipping" (red on the meter). */
  CLIP_DBFS: -3,
  /** Quiet for this long without a break shows the speak-up bubble. */
  SPEAK_UP_AFTER_MS: 3000,
  /** How long the speak-up bubble stays on screen. */
  SPEAK_UP_VISIBLE_MS: 4000,
  /** Digital silence is reported as this, not as -Infinity. */
  FLOOR_DBFS: -100,
  /**
   * The level each of the five meter segments lights at, lowest first. The
   * second step is the quiet threshold, so one lit segment means "quiet".
   */
  METER_STEPS_DBFS: [-55, -45, -35, -25, -12] as const,
} as const;

export type VoiceLevelState = 'silent' | 'quiet' | 'ok' | 'clipping';

/** One 100 ms reading of the microphone. */
export interface VoiceLevelFrame {
  rmsDbfs: number;
  peakDbfs: number;
}

/** Converts a linear amplitude (0..1) to dBFS, with silence held at the floor. */
export function amplitudeToDbfs(amplitude: number): number {
  if (!Number.isFinite(amplitude) || amplitude <= 0) return VOICE_LEVEL.FLOOR_DBFS;
  return Math.max(VOICE_LEVEL.FLOOR_DBFS, 20 * Math.log10(amplitude));
}

/** Root mean square of a block of samples, in dBFS. */
export function rmsDbfs(samples: ArrayLike<number>): number {
  if (samples.length === 0) return VOICE_LEVEL.FLOOR_DBFS;
  let sumSquares = 0;
  for (let i = 0; i < samples.length; i++) {
    sumSquares += samples[i] * samples[i];
  }
  return amplitudeToDbfs(Math.sqrt(sumSquares / samples.length));
}

/** The loudest single sample in a block, in dBFS. */
export function peakDbfs(samples: ArrayLike<number>): number {
  let peak = 0;
  for (let i = 0; i < samples.length; i++) {
    const magnitude = Math.abs(samples[i]);
    if (magnitude > peak) peak = magnitude;
  }
  return amplitudeToDbfs(peak);
}

export function measureFrame(samples: ArrayLike<number>): VoiceLevelFrame {
  return { rmsDbfs: rmsDbfs(samples), peakDbfs: peakDbfs(samples) };
}

/**
 * Clipping is judged on the peak, because a voice whose RMS reached -3 dBFS
 * would be distorted long before. Quiet is judged on the RMS.
 */
export function isClipping(frame: VoiceLevelFrame): boolean {
  return frame.peakDbfs > VOICE_LEVEL.CLIP_DBFS;
}

export function isQuiet(frame: VoiceLevelFrame): boolean {
  return frame.rmsDbfs < VOICE_LEVEL.QUIET_DBFS;
}

/** How many of the five meter segments are lit for this level. */
export function litSegments(levelDbfs: number): number {
  return VOICE_LEVEL.METER_STEPS_DBFS.filter((step) => levelDbfs >= step).length;
}

/**
 * The meter's colour. 'silent' is only the resting state while not listening:
 * a quiet frame, even dead silence, is 'quiet', so the reader sees amber.
 */
export function meterState(frame: VoiceLevelFrame): Exclude<VoiceLevelState, 'silent'> {
  if (isClipping(frame)) return 'clipping';
  if (isQuiet(frame)) return 'quiet';
  return 'ok';
}

/** The meter's height: like litSegments, but a quiet frame always lights the first (amber) LED. */
export function meterSegments(frame: VoiceLevelFrame): number {
  const lit = litSegments(frame.rmsDbfs);
  return isQuiet(frame) ? Math.max(1, lit) : lit;
}

/**
 * Decides when to show "speak up": after SPEAK_UP_AFTER_MS of unbroken quiet
 * while listening, and at most once until reset() (one reading).
 */
export class SpeakUpTracker {
  private quietSince: number | null = null;
  private shown = false;

  /** Feeds one frame. Returns true exactly once: on the frame the prompt should appear. */
  update(frame: VoiceLevelFrame, nowMs: number): boolean {
    if (!isQuiet(frame)) {
      this.quietSince = null;
      return false;
    }
    if (this.quietSince === null) this.quietSince = nowMs;
    if (this.shown) return false;
    if (nowMs - this.quietSince >= VOICE_LEVEL.SPEAK_UP_AFTER_MS) {
      this.shown = true;
      return true;
    }
    return false;
  }

  /** Listening stopped for a while (pause, delete, gate closed): the quiet run starts over. */
  breakRun(): void {
    this.quietSince = null;
  }

  /** A new reading: the prompt may show once more. */
  reset(): void {
    this.quietSince = null;
    this.shown = false;
  }

  get hasShown(): boolean {
    return this.shown;
  }
}

/** The log_text of one READING_VOICE_LEVEL row. */
export interface VoiceLevelSummary {
  median_dbfs: number | null;
  pct_quiet: number;
  pct_clipping: number;
  speak_up_prompts: number;
  samples: number;
}

const roundTo1 = (value: number): number => Math.round(value * 10) / 10;

function median(values: number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 1 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
}

/** Sums up a reading's frames for the log row. Percents are 0..100, one decimal. */
export function summarizeVoiceLevels(frames: VoiceLevelFrame[], speakUpPrompts: number): VoiceLevelSummary {
  const count = frames.length;
  const mid = median(frames.map((frame) => frame.rmsDbfs));
  const percentOf = (n: number): number => (count === 0 ? 0 : roundTo1((n / count) * 100));
  return {
    median_dbfs: mid === null ? null : roundTo1(mid),
    pct_quiet: percentOf(frames.filter(isQuiet).length),
    pct_clipping: percentOf(frames.filter(isClipping).length),
    speak_up_prompts: speakUpPrompts,
    samples: count,
  };
}
